import { Injectable } from "@angular/core";
import { HttpParams } from "@angular/common/http";

import { Action, Selector, StateContext, State } from "@ngxs/store";
import { patch } from "@ngxs/store/operators";
import { tap, finalize, catchError } from "rxjs";

import { OrganizationService } from "src/app/shared/services/organization/organization.service";
import { OrganizationUsers } from "./organization_users.actions";
import { OrganizationUser } from "../../models/organization_users/organization_users.model";
import { PaginatedResult } from "../paginated_result.interface";
import { Filter } from "../../models/filter/filter.model";

export interface OrganizationUsersStateModel extends PaginatedResult<OrganizationUser> {
	selectedUser?: OrganizationUser;
}

@State<OrganizationUsersStateModel>({
	name: "organizationusersstate",
	defaults: {
		items: [],
		filter: {
			q: "",
			page: 1,
			per_page: 10,
			total: 0,
			s: [],
			f: [],
		},
		loading: false,
	},
})
@Injectable()
export class OrganizationUsersState {
	constructor(private organizationService: OrganizationService) {}

	@Selector()
	static filter(state: OrganizationUsersStateModel): Filter {
		return state.filter;
	}

	@Selector()
	static loading(state: OrganizationUsersStateModel): boolean {
		return state.loading;
	}

	@Selector()
	static getOrganizationUsers(state: OrganizationUsersStateModel) {
		return state.items;
	}

	@Selector()
	static getSelectedUser(state: OrganizationUsersStateModel) {
		return state.selectedUser;
	}

	@Action(OrganizationUsers.SetFilter)
	setFilter(ctx: StateContext<OrganizationUsersStateModel>, action: OrganizationUsers.SetFilter) {
		ctx.patchState({
			filter: action.filter,
		});
	}

	@Action(OrganizationUsers.FetchAll)
	fetchOrganizationUsers(ctx: StateContext<OrganizationUsersStateModel>) {
		const state = ctx.getState();
		let filter: any = state.filter
			? Object.entries(state.filter).reduce((acc, [key, value]) => {
					acc[key] = value === null ? "" : value;
					return acc;
			  }, {} as any)
			: {};
		const options = {
			params: new HttpParams({
				fromObject: filter,
			}),
		};
		ctx.patchState({ loading: true });
		return this.organizationService.fetchOrganizationUsers(options).pipe(
			tap((returnData: any) => {
				let users: OrganizationUser[];

				if (Array.isArray(returnData.data)) {
					users = returnData.data;
				} else {
					users = [returnData.data];
				}

				ctx.patchState({
					...ctx.getState(),
					items: users,
					filter: {
						...ctx.getState().filter,
						page: returnData.current_page,
						per_page: returnData.per_page,
						total: returnData.total,
					},
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(OrganizationUsers.FetchUser)
	fetchOrganizationUser(ctx: StateContext<OrganizationUsersStateModel>, action: OrganizationUsers.FetchUser) {
		const state = ctx.getState();
		let filter: any = state.filter
			? Object.entries(state.filter).reduce((acc, [key, value]) => {
					acc[key] = value === null ? "" : value;
					return acc;
			  }, {} as any)
			: {};
		const options = {
			params: new HttpParams({
				fromObject: filter,
			}),
		};
		ctx.patchState({ loading: true });
		return this.organizationService.fetchOrganizationUser(action.userId, options).pipe(
			tap((returnData: any) => {
				let user: OrganizationUser;
				user = returnData.data;

				ctx.patchState({
					...ctx.getState(),
					selectedUser: user,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(OrganizationUsers.Add)
	addOrganizationUser(ctx: StateContext<OrganizationUsersStateModel>, action: OrganizationUsers.Add) {
		ctx.patchState({
			loading: true,
		});
		return this.organizationService.addOrganizationUser(action.user).pipe(
			tap((returnData: any) => {
				const newUser = returnData.data;
				ctx.patchState({
					selectedUser: newUser,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(OrganizationUsers.Update)
	updateOrganizationUser(ctx: StateContext<OrganizationUsersStateModel>, action: OrganizationUsers.Update) {
		ctx.patchState({
			loading: true,
		});
		return this.organizationService.updateOrganizationUser(action.userId, action.user).pipe(
			tap((returnData: any) => {
				const updated = returnData.data;
				const state = ctx.getState();

				ctx.patchState({
					items: state.items.map((ou) => (ou.id === action.userId ? { ...ou, ...updated } : ou)),
					selectedUser: state.selectedUser?.id === action.userId ? { ...state.selectedUser, ...updated } : state.selectedUser,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}

	@Action(OrganizationUsers.Delete)
	deleteProperty(ctx: StateContext<OrganizationUsersStateModel>, action: OrganizationUsers.Delete) {
		ctx.patchState({
			loading: true,
		});
		return this.organizationService.deleteOrganizationUser(action.userId).pipe(
			tap((returnData: any) => {
				const state = ctx.getState();
				ctx.patchState({
					items: state.items.filter((ou: OrganizationUser) => ou.id !== action.userId),
					selectedUser: state.selectedUser?.id === action.userId ? undefined : state.selectedUser,
				});
			}),
			finalize(() => {
				ctx.patchState({
					loading: false,
				});
			})
		);
	}
}
